<template>
  <v-card class="series-overview-card">
    <div class="series-overview-body px-8 pt-8 pb-4">
      <div class="custom-grid">
        <div class="content-section">
          <div class="header-info">
            <div class="text-h6 font-weight-bold mb-4 text-secondary">
              {{
                report.release ? new Date(report.release).getFullYear() : 'N/A'
              }}
            </div>
            <div class="text-h6 font-weight-bold text-secondary">
              {{ report.title }}
            </div>
          </div>
          <div class="navigation-section">
            <div
              class="text-h6 font-weight-bold mb-2 text-secondary report-title"
            >
              {{ gradeDisplay }}
              - {{ gatewayTitle }}
            </div>
            <router-link
              class="text-primary"
              :to="{
                name: 'report-grade-overview',
                params: {
                  id: report.slug,
                  grade: route.params.grade,
                },
              }"
            >
              <v-icon icon="mdi-chevron-left" aria-hidden="true" /> Back to
              {{ gradeDisplay }} Overview
            </router-link>
          </div>
        </div>
        <div class="image-section">
          <div class="heading-card-image">
            <img
              :src="imageSrc"
              :alt="report.title ? `Cover for ${report.title}` : 'Report Cover'"
              class="report-cover"
              width="232"
              height="300"
              @error="handleImageError"
            />
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import type { Report } from '../../store';
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { formatGradeName } from '../../lib/grades';

  const props = defineProps<{
    report: Report;
    selectedGrade?: string;
    gatewayReport?: {
      label: string;
    };
  }>();

  const route = useRoute();

  const fallbackCoverUrl =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMzIgMzAwIj48cmVjdCB3aWR0aD0iMjMyIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2ViZjdmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiMwMDdlYWIiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPlJlcG9ydCBDb3ZlcjwvdGV4dD48L3N2Zz4=';

  const imageSrc = ref(props.report.cover || fallbackCoverUrl);

  watch(
    () => props.report.cover,
    (newCover) => {
      imageSrc.value = newCover || fallbackCoverUrl;
    },
  );

  function handleImageError() {
    imageSrc.value = fallbackCoverUrl;
  }

  const gradeDisplay = computed(() => {
    if (!props.selectedGrade) return '';

    // Find the matching grade in the report's grades
    const matchingGrade = props.report.report_grades?.find(
      (grade) => grade.slug === props.selectedGrade,
    );

    // Use the grade's label/title if available, otherwise format the slug
    if (matchingGrade) {
      return (
        matchingGrade.label ||
        matchingGrade.title ||
        formatGradeName(matchingGrade.name)
      );
    }

    // Fallback to formatting the slug if no matching grade found
    const gradeSlug = props.selectedGrade.toLowerCase();
    const gradePart = gradeSlug.replace('-grade', '');
    return formatGradeName(gradePart);
  });

  const gatewayNumber = computed(() => {
    const gateway = route.params.gateway as string;
    if (!gateway) return '1';

    const gatewayMap: Record<string, string> = {
      'gateway-one': '1',
      'gateway-two': '2',
      'gateway-three': '3',
      'gateway-four': '4',
    };

    return gatewayMap[gateway] || '1';
  });

  // Special handling for MLL gateway
  const gatewayTitle = computed(() => {
    const gateway = route.params.gateway as string;
    if (
      gateway === 'gateway-four' ||
      gateway === 'multilingual-learner-supports'
    ) {
      return props.gatewayReport?.label || 'Multilingual Learner Supports';
    }
    return `Gateway ${gatewayNumber.value}`;
  });
</script>

<style lang="scss" scoped>
  .series-overview-card {
    .overview-info-block {
      .text-caption {
        text-transform: uppercase !important;
      }
      .text-subcaption {
        font-size: 16px;
        font-weight: bold;
        color: rgb(var(--v-theme-secondary));
        &.alt {
          font-size: 12px;
          font-weight: normal;
        }
      }
      &.gateway-status {
        .text-subcaption {
          font-size: 14px;
        }
      }
    }
    .series-overview-body {
      .report-cover {
        max-width: 232px;
      }
      .report-title {
        font-family: 'rockwell', sans-serif;
        font-size: 48px !important;
        font-weight: normal !important;
      }
    }
    .series-overview-footer {
      background-color: #f0f3f5;
      .v-col {
        .overview-info-block {
          width: 100%;
          border-right: 1px solid rgba(var(--v-theme-blue), 0.25);
        }
        &:last-child {
          .overview-info-block {
            border-right: none;
          }
        }
      }
    }
    &.v-card a.text-primary {
      font-size: 14px !important;
    }
  }

  /* Custom Grid Layout */
  .custom-grid {
    display: flex;
    gap: 24px;
    align-items: stretch;
    min-height: 300px;
  }

  .content-section {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 280px;
  }

  .image-section {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  .header-info {
    flex-shrink: 0;
  }

  .navigation-section {
    margin-top: auto;
    padding-top: 2rem;
    flex-shrink: 0;
  }

  .heading-card-image {
    border: 1px solid var(--card-border-grey);
    display: inline-block;
    .report-cover {
      display: block;
      width: 232px;
      height: 300px;
      max-width: 232px;
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .custom-grid {
      flex-direction: column;
      gap: 16px;
    }

    .image-section {
      order: -1;
      justify-content: center;
    }
  }

  /* Print styles */
  @media print {
    /* Hide card styling in print */
    .series-overview-card {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    /* Force print layout */
    .series-overview-body {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      display: block !important;
    }

    /* NUCLEAR OPTION: Force everything to be block layout */
    .custom-grid {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    /* Force image section to be on its own line - 100% width */
    .image-section {
      display: block !important;
      width: 100% !important;
      flex: 0 0 100% !important;
      max-width: 100% !important;
      text-align: center !important;
      margin: 0 auto 20px auto !important;
      padding: 0 !important;
      page-break-inside: avoid !important;
    }

    /* Center everything about the image */
    .heading-card-image {
      border: none !important;
      display: block !important;
      width: 200px !important;
      max-width: 200px !important;
      margin: 0 auto !important;
      text-align: center !important;
    }

    /* Force image to be centered */
    .report-cover {
      width: 180px !important;
      max-width: 180px !important;
      height: auto !important;
      margin: 0 auto !important;
      display: block !important;
      position: static !important;
      left: auto !important;
      right: auto !important;
      transform: none !important;
      float: none !important;
    }

    /* Content section comes AFTER image on new line */
    .content-section {
      display: block !important;
      width: 100% !important;
      clear: both !important;
      margin: 20px 0 0 0 !important;
      padding: 0 !important;
    }

    /* Force color printing for all elements */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
</style>
